import * as React from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Link } from "wouter"
import { HelpCircle, X, BookOpen, ArrowRight, ArrowLeft, Lightbulb, ExternalLink, PlayCircle } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { HELP_ARTICLES, getArticlesForPage, getArticleById, type HelpArticle } from "@/lib/help-articles"
import { useMyFeatures, useMe, type Permission } from "@/lib/auth"
import { recordHelpEvent } from "@/lib/help-analytics"
import {
  getWalkthroughsForPage,
  useWalkthroughs,
  useWalkthroughStates,
} from "@/lib/walkthroughs"
import { VCHelpVote } from "./VCHelpVote"

interface HelpDrawerContextValue {
  openHelp: (pageId: string) => void
  /** Open the drawer directly at a specific article (Task #2088). */
  openHelpArticle: (articleId: string) => void
  closeHelp: () => void
  isOpen: boolean
}

const HelpDrawerContext = createContext<HelpDrawerContextValue>({
  openHelp: () => undefined,
  openHelpArticle: () => undefined,
  closeHelp: () => undefined,
  isOpen: false,
})

export function useHelpDrawer() {
  return useContext(HelpDrawerContext)
}

function ArticleCard({
  article,
  onClose,
}: {
  article: HelpArticle
  onClose: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  const bodyLines = article.body
    .split("\n")
    .filter((l) => l.trim() && !l.startsWith("#"))
    .slice(0, 3)
    .join(" ")
    .replace(/\*\*/g, "")
    .replace(/\|.*\|/g, "")
    .trim()

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start gap-2">
        <span className="text-xl leading-none mt-0.5" aria-hidden="true">
          {article.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold leading-snug">{article.title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {article.summary}
          </p>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3">
          {article.tips.length > 0 && (
            <div className="rounded-md bg-muted/50 p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Lightbulb className="h-3.5 w-3.5 text-yellow-500" />
                Quick tips
              </div>
              <ul className="space-y-1">
                {article.tips.map((tip, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-2">
                    <span className="text-muted-foreground/60 shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground line-clamp-4">{bodyLines}</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-primary hover:underline"
        >
          {expanded ? "Show less" : "Show tips"}
        </button>
        <Link href="/help">
          <a
            onClick={onClose}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Full article <ArrowRight className="h-3 w-3" />
          </a>
        </Link>
      </div>
    </div>
  )
}

/** Full single-article view inside the drawer (deep-open target). */
function ArticleDetailView({
  article,
  onBack,
  onClose,
}: {
  article: HelpArticle
  onBack: (() => void) | null
  onClose: () => void
}) {
  useEffect(() => {
    recordHelpEvent({ eventType: "article_view", articleId: article.id, context: "drawer" })
  }, [article.id])

  const bodyText = article.body
    .split("\n")
    .filter((l) => l.trim() && !l.startsWith("#") && !l.startsWith("|"))
    .join(" ")
    .replace(/\*\*/g, "")
    .trim()

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="pb-4 border-b">
        <div className="flex items-center justify-between">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            Help article
          </SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <X className="h-4 w-4" />
              <span className="sr-only">Close help drawer</span>
            </Button>
          </SheetClose>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto py-4 space-y-4" data-testid={`drawer-article-${article.id}`}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
        )}
        <div className="flex items-start gap-2">
          <span className="text-2xl leading-none" aria-hidden="true">{article.emoji}</span>
          <div>
            <Badge variant="secondary" className="text-[10px] font-normal mb-1">
              {article.module}
            </Badge>
            <h3 className="text-sm font-semibold leading-snug">{article.title}</h3>
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{article.summary}</p>
        <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{bodyText}</p>
        {article.tips.length > 0 && (
          <div className="rounded-md bg-muted/50 p-3 space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <Lightbulb className="h-3.5 w-3.5 text-yellow-500" />
              Quick tips
            </div>
            <ul className="space-y-1">
              {article.tips.map((tip, i) => (
                <li key={i} className="text-xs text-muted-foreground flex gap-2">
                  <span className="text-muted-foreground/60 shrink-0">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <VCHelpVote articleId={article.id} />
      </div>

      <div className="pt-4 border-t">
        <Link href={`/help?article=${encodeURIComponent(article.id)}`}>
          <a
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full rounded-md border bg-muted/50 hover:bg-muted px-3 py-2 text-sm font-medium transition-colors"
            data-testid="drawer-open-in-help-centre"
          >
            <ExternalLink className="h-4 w-4" />
            Open in Help Centre
          </a>
        </Link>
      </div>
    </div>
  )
}

/** "Take the tour" section — walkthroughs registered for the current page. */
function PageTours({ pageId, onClose }: { pageId: string; onClose: () => void }) {
  const { start, canSee } = useWalkthroughs()
  const { data } = useWalkthroughStates()
  const tours = getWalkthroughsForPage(pageId).filter(canSee)
  if (tours.length === 0) return null
  return (
    <div className="space-y-2">
      {tours.map((t) => {
        const seen = data?.states.some((s) => s.walkthroughId === t.id)
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              onClose()
              start(t.id, { restart: true })
            }}
            className="w-full flex items-center gap-2 rounded-lg border bg-card hover:border-primary/50 p-3 text-left transition-all"
            data-testid={`drawer-tour-${t.id}`}
          >
            <PlayCircle className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold">
                {seen ? `Restart: ${t.title}` : t.title}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{t.description}</div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

function HelpDrawerContent({
  pageId,
  onClose,
}: {
  pageId: string
  onClose: () => void
}) {
  const { hasFeature } = useMyFeatures()
  const { has } = useMe()

  const canSee = (a: HelpArticle) =>
    (!a.featureKey || hasFeature(a.featureKey)) &&
    (!a.requiredPermission || has(a.requiredPermission as Permission))

  const articles = useMemo(() => {
    const pageArticles = getArticlesForPage(pageId).filter(canSee)
    if (pageArticles.length >= 3) return pageArticles.slice(0, 3)
    // Supplement with related articles to reach up to 3
    const seenIds = new Set(pageArticles.map((a) => a.id))
    const relatedIds = pageArticles.flatMap((a) => a.relatedIds)
    const related = relatedIds
      .map((id) => HELP_ARTICLES.find((a) => a.id === id))
      .filter((a): a is HelpArticle => !!a && !seenIds.has(a.id) && canSee(a))
    const deduped: HelpArticle[] = []
    for (const a of related) {
      if (!deduped.find((x) => x.id === a.id)) deduped.push(a)
    }
    const combined = [...pageArticles, ...deduped].slice(0, 3)
    if (combined.length > 0) return combined
    return HELP_ARTICLES.filter(canSee).slice(0, 3)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, hasFeature, has])

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="pb-4 border-b">
        <div className="flex items-center justify-between">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            Help for this page
          </SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <X className="h-4 w-4" />
              <span className="sr-only">Close help drawer</span>
            </Button>
          </SheetClose>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        <PageTours pageId={pageId} onClose={onClose} />
        {articles.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <BookOpen className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No articles found for this page yet.</p>
          </div>
        ) : (
          articles.map((article) => (
            <ArticleCard key={article.id} article={article} onClose={onClose} />
          ))
        )}
      </div>

      <div className="pt-4 border-t">
        <Link href="/help">
          <a
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full rounded-md border bg-muted/50 hover:bg-muted px-3 py-2 text-sm font-medium transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            View all help articles
          </a>
        </Link>
      </div>
    </div>
  )
}

export function HelpDrawerProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentPageId, setCurrentPageId] = useState<string>("")
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null)
  const { hasFeature } = useMyFeatures()
  const { has } = useMe()

  const openHelp = useCallback((pageId: string) => {
    setCurrentPageId(pageId)
    setCurrentArticleId(null)
    setIsOpen(true)
  }, [])

  const openHelpArticle = useCallback((articleId: string) => {
    setCurrentArticleId(articleId)
    setIsOpen(true)
  }, [])

  const closeHelp = useCallback(() => {
    setIsOpen(false)
    setCurrentArticleId(null)
  }, [])

  const value = useMemo(
    () => ({ openHelp, openHelpArticle, closeHelp, isOpen }),
    [openHelp, openHelpArticle, closeHelp, isOpen],
  )

  // Deep-opened article must still pass feature/permission gating — a link
  // for a disabled feature never renders content.
  const article = currentArticleId ? getArticleById(currentArticleId) : undefined
  const articleVisible =
    !!article &&
    (!article.featureKey || hasFeature(article.featureKey)) &&
    (!article.requiredPermission || has(article.requiredPermission as Permission))

  return (
    <HelpDrawerContext.Provider value={value}>
      {children}
      <Sheet open={isOpen} onOpenChange={(open) => !open && closeHelp()}>
        <SheetContent
          side="right"
          className="w-[360px] sm:w-[400px] flex flex-col p-6"
          aria-label="Help drawer"
        >
          {isOpen &&
            (articleVisible && article ? (
              <ArticleDetailView
                article={article}
                onBack={currentPageId ? () => setCurrentArticleId(null) : null}
                onClose={closeHelp}
              />
            ) : (
              <HelpDrawerContent pageId={currentPageId} onClose={closeHelp} />
            ))}
        </SheetContent>
      </Sheet>
    </HelpDrawerContext.Provider>
  )
}

export function HelpIconButton({
  pageId,
  className,
}: {
  pageId: string
  className?: string
}) {
  const { openHelp } = useHelpDrawer()
  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={() => openHelp(pageId)}
      aria-label="Open help"
      data-testid={`btn-help-${pageId}`}
    >
      <HelpCircle className="h-4 w-4 text-muted-foreground" />
    </Button>
  )
}

